import { useEffect, useState } from "react";
import { Star, Loader2, MessageSquare } from "lucide-react";
import { format, parseISO } from "date-fns";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Fetches and renders published reviews. Used by both the full public page and
 * the lean embed. `compact` shrinks paddings for the WordPress iframe.
 */
export default function PublicReviewsList({ compact = false }) {
  const [state, setState] = useState({ reviews: [], aggregate: { count: 0, average: 0 } });
  const [loading, setLoading] = useState(true);
  const [minRating, setMinRating] = useState(0);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios
      .get(`${BACKEND_URL}/api/reviews/public`, { params: minRating ? { min_rating: minRating } : {} })
      .then(({ data }) => { if (mounted) setState(data); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [minRating]);

  const avg = state.aggregate.average || 0;
  const total = state.aggregate.count || 0;

  return (
    <div className={compact ? "px-4 py-6" : "max-w-5xl mx-auto px-4 sm:px-6 py-10"}>
      <div className={compact ? "mb-6" : "mb-10"}>
        <div className="flex items-center gap-3 mb-2">
          <MessageSquare className="w-6 h-6" style={{ color: "var(--neon-cyan)" }} />
          <h1 className={`font-display font-black text-white uppercase tracking-wider ${compact ? "text-2xl" : "text-3xl sm:text-4xl"}`}>
            Player Reviews
          </h1>
        </div>
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((s) => (
              <Star key={s} className="w-5 h-5" strokeWidth={1.5}
                style={{ color: s <= Math.round(avg) ? "#ffb020" : "#334155",
                         fill: s <= Math.round(avg) ? "#ffb020" : "transparent" }} />
            ))}
          </div>
          <div className="text-white font-display font-bold text-lg">{avg.toFixed(1)}</div>
          <div className="text-slate-400 text-sm">·  {total} {total === 1 ? "review" : "reviews"}</div>
        </div>

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          {[0, 5, 4, 3].map((r) => (
            <button
              key={r}
              onClick={() => setMinRating(r)}
              className="chip"
              data-testid={`filter-min-${r}`}
              style={{
                background: minRating === r ? "var(--neon-cyan)" : "rgba(8,11,22,0.6)",
                border: `1px solid ${minRating === r ? "var(--neon-cyan)" : "rgba(120,150,220,0.18)"}`,
                color: minRating === r ? "#05060d" : "#cbd5e1",
                fontWeight: 700,
              }}
            >
              {r === 0 ? "All ratings" : `${r}★ & up`}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading reviews…
        </div>
      ) : state.reviews.length === 0 ? (
        <div className="text-slate-500 py-10 text-center border border-dashed border-slate-800 rounded-2xl">
          No reviews yet. Come by the lounge — first drop coming soon.
        </div>
      ) : (
        <div className={`grid gap-4 ${compact ? "grid-cols-1" : "sm:grid-cols-2"}`}>
          {state.reviews.map((r) => (
            <ReviewCard key={r.id} review={r} compact={compact} />
          ))}
        </div>
      )}
    </div>
  );
}

const ReviewCard = ({ review, compact }) => {
  const initials = (review.author_name || "R").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div
      className="rounded-2xl border p-5 transition-all"
      data-testid={`public-review-${review.id}`}
      style={{
        background: "rgba(14,18,34,0.72)",
        borderColor: "rgba(120,150,220,0.16)",
        backdropFilter: "blur(8px)",
      }}
    >
      <div className="flex items-center gap-3 mb-3">
        {review.author_avatar ? (
          <img src={review.author_avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full grid place-items-center font-display font-black text-black"
               style={{ background: "linear-gradient(135deg, var(--neon-cyan), var(--neon-violet))" }}>
            {initials}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold truncate">{review.author_name}</p>
          <p className="text-slate-500 text-xs">{format(parseISO(review.created_at), "PPP")}</p>
        </div>
        <div className="flex items-center gap-0.5">
          {[1, 2, 3, 4, 5].map((s) => (
            <Star key={s} className="w-3.5 h-3.5" strokeWidth={1.5}
              style={{ color: s <= review.rating ? "#ffb020" : "#334155",
                       fill: s <= review.rating ? "#ffb020" : "transparent" }} />
          ))}
        </div>
      </div>
      {review.title && (
        <p className="text-white font-display font-bold text-base mb-1">{review.title}</p>
      )}
      <p className={`text-slate-300 whitespace-pre-wrap ${compact ? "text-sm" : ""}`}>{review.body}</p>
    </div>
  );
};
